import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import LeaderboardFilterSelect from './LeaderboardFilterSelect';
import useLeaderboardStore, {
  DimensionType,
  DimensionTimeframe,
} from '../../../store/zustand/useLeaderboardStore';
import {
  getDimensionValue,
  getWeekDimensionFormattedDate,
} from '../Leaderboard.utils';
import { getWeek } from 'date-fns';
import { CustomDateInput, CustomSelect } from '../Leaderboard.styles';
import useOrgStore from '../../../store/zustand/useOrgStore';

const formatDateForWeekInput = (dateStr: string) => {
  const date = new Date(dateStr);
  const year = date.getFullYear();
  const week = getWeek(date, { weekStartsOn: 1 }); // Start week on Monday
  return `${year}-W${week.toString().padStart(2, '0')}`;
};

const generateYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = 2017; year <= currentYear; year++) {
    years.push(year);
  }
  return years;
};

const LeaderboardFilters: React.FC = () => {
  const {
    leaderboardViews,
    leaderboardFilters,
    viewsStack,
    applyView,
    updateFilters,
  } = useLeaderboardStore();
  const { circleLevels } = useOrgStore();
  const theme = useTheme();

  const timeframeInputs: Record<DimensionTimeframe, React.ReactNode> = {
    D: (
      <>
        <Typography
          style={{ fontSize: 16, fontWeight: 600, color: theme.custom.text }}
        >
          Date
        </Typography>
        <CustomDateInput
          type="date"
          value={leaderboardFilters?.dimensionValue || ''}
          onChange={(e) => {
            updateFilters({
              dimensionValue: e.target.value,
            });
          }}
        />
      </>
    ),
    W: (
      <>
        <Typography
          style={{ fontSize: 16, fontWeight: 600, color: theme.custom.text }}
        >
          Week
        </Typography>
        <CustomDateInput
          type="week"
          value={
            leaderboardFilters?.dimensionValue
              ? formatDateForWeekInput(leaderboardFilters?.dimensionValue)
              : ''
          }
          onChange={(e) => {
            const formattedDate = getWeekDimensionFormattedDate(e.target.value);
            updateFilters({
              dimensionValue: formattedDate,
            });
          }}
        />
      </>
    ),
    M: (
      <>
        <Typography
          style={{ fontSize: 16, fontWeight: 600, color: theme.custom.text }}
        >
          Month
        </Typography>
        <CustomDateInput
          type="month"
          value={leaderboardFilters?.dimensionValue || ''}
          onChange={(e) => {
            updateFilters({
              dimensionValue: e.target.value,
            });
          }}
        />
      </>
    ),
    Y: (
      <>
        <Typography
          style={{ fontSize: 16, fontWeight: 600, color: theme.custom.text }}
        >
          Year
        </Typography>
        <CustomSelect
          value={leaderboardFilters?.dimensionValue || ''}
          onChange={(e) => {
            updateFilters({
              dimensionValue: e.target.value,
            });
          }}
        >
          <option value="">Select Year</option>
          {generateYearOptions().map((year) => (
            <option key={year} value={year.toString()}>
              {year}
            </option>
          ))}
        </CustomSelect>
      </>
    ),
  };

  return (
    <Box style={{ display: 'flex', flexDirection: 'row', gap: 10 }}>
      <LeaderboardFilterSelect
        label="View"
        value={leaderboardFilters?.viewID || ''}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          applyView(e.target.value)
        }
        options={[
          { value: '', label: 'All KPIs' },
          ...(leaderboardViews?.map((view: any) => ({
            value: view.id,
            label: view.name,
          })) || []),
        ]}
      />
      <LeaderboardFilterSelect
        label="Team / People"
        value={leaderboardFilters?.dimensionType as DimensionType}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          updateFilters({ dimensionType: e.target.value as DimensionType })
        }
        disabled={viewsStack.length === circleLevels.length + 1}
        options={[
          { value: 'circle', label: 'Team' },
          { value: 'person', label: 'People' },
        ]}
      />
      <LeaderboardFilterSelect
        label="Timeframe"
        value={leaderboardFilters?.dimensionTimeframe as DimensionTimeframe}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          updateFilters({
            dimensionTimeframe: e.target.value as DimensionTimeframe,
            dimensionValue: getDimensionValue(
              e.target.value as DimensionTimeframe
            ),
          })
        }
        options={[
          { value: 'D', label: 'Day' },
          { value: 'W', label: 'Week' },
          { value: 'M', label: 'Month' },
          { value: 'Y', label: 'Year' },
        ]}
      />
      <Box
        style={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 6 }}
      >
        {timeframeInputs[
          leaderboardFilters?.dimensionTimeframe as DimensionTimeframe
        ] || null}
      </Box>
    </Box>
  );
};

export default LeaderboardFilters;
